import { useCallback, useEffect, useMemo } from "react";
import { FeatureFlags } from "featureFlags";
import {
  isUserDeleted,
  formatUserName,
  type PerformanceWithAccountFilter,
  canUseChildAccount,
} from "features/Library/Common/utils/performanceUtils";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { fetchPeopleOverviewUsersTablePaginated, fetchPeopleOverviewUsersTableExport } from "../utils";
import { type ColumnOption } from "interfaces/columnOptions";
import { type PeopleOverviewUsersItem } from "features/Reporting/types/people";
import { TextTruncate } from "components";
import { Tooltip } from "components/common/tooltip";
import { GenericPerformanceListWithServerPagination } from "features/Reporting/Content/shared/GenericPerformanceListWithServerPagination";
import { RouteNames } from "enums";

import "../../peopleStyles.scss";
import { MoboLink } from "components/MoboLink/MoboLink";
import { bindActionCreators, type Dispatch } from "@reduxjs/toolkit";
import { bindAction } from "interfaces";
import { setExportAction, setHasDataToExport } from "features/Reporting/state/export/exportSlice";
import { connect, type ConnectedProps } from "react-redux";
import { type ExportAvailability } from "features/Reporting/Content/shared/GenericPerformanceListWithServerPagination";
import { type PaginationParams } from "features/Library/Common/models";

export type Props = {
  filter: PerformanceWithAccountFilter;
  disableLinks: boolean;
  childAccountId?: number;
  hasCreateAccountsAddOn?: boolean;
} & PropsFromRedux;

type UserColumnOption = ColumnOption<PeopleOverviewUsersItem> & { sortKey?: string };

export const EnterpriseTable = ({
  filter,
  disableLinks,
  setExport,
  setIsExportEnabled,
  usersExport,
  childAccountId,
  hasCreateAccountsAddOn,
}: Props) => {
  const reportEnabled = !!useFeatureFlag(FeatureFlags.PeopleReports);
  const childAccountEnabled = canUseChildAccount(childAccountId, hasCreateAccountsAddOn);

  const fetchUsersData = useCallback(
    (params: PaginationParams, signal?: AbortSignal) => fetchPeopleOverviewUsersTablePaginated(filter, params, signal),
    [filter],
  );
  const queryKey = useMemo(() => ["PeopleOverviewUsersTable", filter] as const, [filter]);

  // Setting appropriate export method
  useEffect(() => {
    setExport({
      method: usersExport,
      args: [filter],
    });
  }, [filter, setExport, usersExport]);

  const updateExportDataState = useCallback(
    ({ hasDataToExport }: ExportAvailability) =>
      setIsExportEnabled({
        hasDataToExport,
        tooltipContent: "No data available for export. There are no users with activity data.",
      }),
    [setIsExportEnabled],
  );

  const columns: UserColumnOption[] = useMemo(
    () => [
      {
        name: "Name",
        width: 7,
        isSortable: true,
        sortKey: "UserFullName",
        render(item) {
          const shouldShowLink = !isUserDeleted(item.UserFullName) && !disableLinks && item.UserId !== null;
          const formattedName = formatUserName(item.UserFullName);
          return (
            <Tooltip
              target={
                <TextTruncate>
                  {shouldShowLink ? (
                    <MoboLink
                      target={childAccountEnabled ? "_blank" : undefined}
                      accountId={childAccountEnabled ? childAccountId : undefined}
                      to={`/${RouteNames.peopleUsers}/${item.UserId}`}
                    >
                      {formattedName}
                    </MoboLink>
                  ) : (
                    formattedName
                  )}
                </TextTruncate>
              }
              content={formattedName}
            />
          );
        },
      },
      {
        name: "Department",
        width: 4,
        isSortable: true,
        sortKey: "UserDepartment",
        render(item) {
          return (
            <Tooltip
              target={<TextTruncate>{item.UserDepartment || "-"}</TextTruncate>}
              content={item.UserDepartment || "-"}
            />
          );
        },
      },
      {
        name: "Interactions",
        width: 4,
        isSortable: true,
        sortKey: "InteractionCount",
        render(item) {
          return <span>{item.InteractionCount.toLocaleString()}</span>;
        },
      },
    ],
    [disableLinks, childAccountEnabled, childAccountId],
  );

  if (!reportEnabled) {
    return null;
  }

  return (
    <div className="tabSpacer">
      <GenericPerformanceListWithServerPagination
        title="Most Active Users"
        columns={columns}
        filter={filter}
        fetchPage={fetchUsersData}
        queryKeyBase={queryKey}
        hasDataForExport={updateExportDataState}
      />
    </div>
  );
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  setIsExportEnabled: bindAction(setHasDataToExport, dispatch),
  setExport: bindAction(setExportAction, dispatch),
  usersExport: bindActionCreators(fetchPeopleOverviewUsersTableExport, dispatch),
});

const connector = connect(null, mapDispatchToProps);
export type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(EnterpriseTable);
export default ConnectedComponent;
